import React, { useEffect, useState } from "react";
import axios from "axios";

const api = process.env.REACT_APP_API_URL;

const toggleFields = [
  ["autoRotate", "Automatically rotate metrics"],
  ["showQR", "Show QR code"],
  ["excludeAdmin", "Exclude circle owner"],
  ["rtlMode", "Hebrew / RTL layout"],
  ["showWeeklyWinners", "Show weekly winners"],
  ["showAllTimeWinners", "Show all-time winners"],
];

const selectFields = [
  ["refreshIntervalMinutes", "Refresh interval (minutes)", [1, 2, 5, 10, 15]],
  [
    "displayScale",
    "Display size (%)",
    [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
  ],
  ["topLeadersCount", "Top leaders", [1, 2, 3, 4, 5]],
  ["rotatingRowsCount", "Rotating rows", [3, 4, 5, 6, 7, 8, 9, 10]],
  [
    "memberRotationSeconds",
    "Member rotation (seconds; 0 = off)",
    [0, 5, 10, 15, 20, 30, 45, 60],
  ],
  [
    "weeklyWinnersIntervalMinutes",
    "Weekly winners interval (minutes)",
    [1, 2, 5, 10, 15],
  ],
  [
    "allTimeWinnersIntervalMinutes",
    "All-time winners interval (minutes)",
    [1, 2, 5, 10, 15],
  ],
];

function authHeaders() {
  return {
    Authorization: localStorage.getItem("jwtToken") || "",
  };
}

export default function AdminLeaderboard({ circleId }) {
  const [circle, setCircle] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setSettings(null);
    setCircle(null);
    setError("");
    setMessage("");

    axios
      .get(api + `circles/admin/${circleId}/leaderboard`, {
        headers: authHeaders(),
      })
      .then(({ data }) => {
        if (cancelled) return;
        setCircle(data.circle);
        setSettings(data.leaderboardSettings);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.message || "Unable to load settings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [circleId]);

  const update = (field, value) => {
    setSettings((previous) => ({
      ...previous,
      [field]: value,
    }));
    setMessage("");
    setError("");
  };

  const updateMetric = (metric, value) => {
    setSettings((previous) => ({
      ...previous,
      visibleMetrics: {
        ...previous.visibleMetrics,
        [metric]: value,
      },
    }));
    setMessage("");
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    try {
      await axios.patch(
        api +
          `circles/leaderboard/public/${encodeURIComponent(
            circle.leaderboardShareToken,
          )}/settings`,
        { leaderboardSettings: settings },
        { headers: authHeaders() },
      );

      setMessage("Settings saved.");
    } catch (err) {
      setError(err.response?.data?.message || "Unable to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p role="status">Loading leaderboard settings…</p>;

  if (!settings || !circle) {
    return <div className="alert alert-danger">{error}</div>;
  }

  const canSave =
    circle.leaderboardShareTokenEnabled &&
    Boolean(circle.leaderboardShareToken);

  return (
    <form onSubmit={save} className="ba-panel ba-leaderboard">
      <h3>Leaderboard settings - {circle.name}</h3>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success" role="status">
          {message}
        </div>
      )}

      {!canSave && (
        <div className="alert alert-warning">
          Enable this branch’s leaderboard sharing in the app before saving
          settings here.
        </div>
      )}

      <fieldset disabled={saving}>
        <h5 className="mt-4">Launch</h5>

        <div className="ba-launch-row">
          <div>
            <label htmlFor="leaderboard-start-date">Start date</label>
            <input
              id="leaderboard-start-date"
              className="form-control"
              type="date"
              value={settings.startDate || ""}
              onChange={(event) =>
                update("startDate", event.target.value || null)
              }
            />
          </div>

          <div>
            <label htmlFor="leaderboard-start-time">Start time</label>
            <input
              id="leaderboard-start-time"
              className="form-control"
              type="time"
              value={settings.startTime || ""}
              onChange={(event) =>
                update("startTime", event.target.value || null)
              }
            />
          </div>
        </div>

        <h5 className="mt-4">Display and winners</h5>

        {toggleFields.map(([field, label]) => (
          <label key={field} style={{ display: "block", marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={settings[field] === true}
              onChange={(event) => update(field, event.target.checked)}
              style={{ marginRight: 10 }}
            />
            {label}
          </label>
        ))}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 16,
          }}
        >
          {selectFields.map(([field, label, options]) => (
            <div key={field}>
              <label htmlFor={`leaderboard-${field}`}>{label}</label>
              <select
                id={`leaderboard-${field}`}
                className="form-control"
                value={settings[field]}
                onChange={(event) => update(field, Number(event.target.value))}
              >
                {options.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <h5 className="mt-4">Visible metrics</h5>

        {["steps", "distance", "points", "checkins"].map((metric) => (
          <label key={metric} style={{ display: "block", marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={settings.visibleMetrics?.[metric] === true}
              disabled={metric === "steps" && !circle.showSteps}
              onChange={(event) => updateMetric(metric, event.target.checked)}
              style={{ marginRight: 10 }}
            />
            {metric === "checkins"
              ? "Check-ins"
              : metric.charAt(0).toUpperCase() + metric.slice(1)}
            {metric === "steps" && !circle.showSteps
              ? " (not enabled for this branch)"
              : ""}
          </label>
        ))}

        <button
          className="btn btn-primary mt-3"
          type="submit"
          disabled={!canSave || saving}
        >
          {saving ? "Saving…" : "Save settings"}
        </button>
      </fieldset>
    </form>
  );
}
